<template>
  <v-card class="mb-4 toolbar-card" elevation="1">
    <v-card-text>
      <!-- 上部行: 図面操作 -->
      <v-row align="center">
        <!-- 図面選択ドロップダウン（通常表示） -->
        <v-col v-if="!isEditingName" cols="auto">
          <span class="text-body-2 font-weight-medium">{{ $t('label.floor_plan') }}</span>
          <v-select
            :value="currentFloorPlan ? currentFloorPlan.id : null"
            :items="floorPlanOptions"
            :disabled="!currentFloorPlan"
            :placeholder="!currentFloorPlan ? $t('label.no_floor_plan') : undefined"
            item-value="value"
            item-text="text"
            dense
            hide-details
            outlined
            class="ml-2 d-inline-block compact-select"
            style="width: 200px;"
            :aria-label="$t('label.floor_plan')"
            @change="$emit('change', $event)"
          />
        </v-col>

        <!-- 図面名編集フィールド（currentFloorPlan がある場合のみ編集可能） -->
        <v-col v-if="isEditingName && currentFloorPlan" cols="auto">
          <span class="text-body-2 font-weight-medium">{{ $t('label.floor_plan') }}</span>
          <v-text-field
            v-model="editingName"
            dense
            hide-details
            outlined
            autofocus
            class="ml-2 d-inline-block compact-select"
            style="width: 200px;"
            :aria-label="$t('label.edit_name')"
            @keydown.enter="saveNameEdit"
            @keydown.esc="cancelNameEdit"
          />
        </v-col>

        <!-- 編集ボタンエリア -->
        <v-col cols="auto">
          <span v-if="!isEditingName">
            <v-btn icon small :disabled="!currentFloorPlan" :aria-label="$t('action.edit_name')" @click="startNameEdit">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </span>
          <span v-if="isEditingName">
            <v-btn icon small color="primary" :aria-label="$t('action.save_name')" @click="saveNameEdit">
              <v-icon>mdi-check</v-icon>
            </v-btn>
          </span>
        </v-col>

        <!-- 図面追加ボタン（currentFloorPlan の有無に関わらず常時表示、元の位置を維持） -->
        <v-col cols="auto">
          <v-btn icon small :aria-label="$t('action.add')" data-testid="add-floor-plan" @click="$emit('add')">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-col>

        <!-- 図面削除ボタン -->
        <v-col cols="auto">
          <v-btn
            icon
            small
            :loading="deleting"
            :disabled="deleting || !currentFloorPlan"
            :aria-label="$t('action.delete')"
            @click="$emit('delete')"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-col>

        <v-spacer />

        <!-- 未保存変更メッセージ -->
        <v-col v-if="hasUnsavedChanges && !saving" cols="auto" class="py-0">
          <v-alert type="warning" dense text class="mb-0 pa-1">
            <span class="text-caption">{{ $t('msg.unsaved_changes') }}</span>
          </v-alert>
        </v-col>

        <!-- 保存中メッセージ -->
        <v-col v-if="saving" cols="auto" class="py-0">
          <v-alert type="info" dense text class="mb-0 pa-1">
            <span class="text-caption">{{ $t('msg.saving') }}</span>
          </v-alert>
        </v-col>

        <!-- 保存・取り消しボタン -->
        <v-col v-if="hasUnsavedChanges" cols="auto" class="py-0">
          <v-btn
            color="primary"
            small
            :loading="saving"
            :disabled="saving"
            class="mr-2"
            data-testid="save-floor-plan"
            @click="$emit('save')"
          >
            <v-icon x-small left>mdi-content-save</v-icon>
            {{ $t('action.save') }}
          </v-btn>
          <v-btn text small @click="$emit('cancel')">
            <v-icon x-small left>mdi-close</v-icon>
            {{ $t('action.cancel') }}
          </v-btn>
        </v-col>
      </v-row>

      <!-- 下部行: フィルタ + ダウンロード -->
      <v-row align="end" class="mt-4">
        <!-- 月選択 -->
        <v-col cols="auto">
          <div class="control-group">
            <span class="control-group__label">{{ $t('label.month') }}</span>
            <div class="d-inline-block compact-select" style="width: 90px;">
              <month-field
                :value="selectedMonth"
                dense
                outlined
                hide-details
                data-testid="month-selector"
                @input="value => $emit('month-change', value)"
              />
            </div>
          </div>
        </v-col>

        <!-- 描画モード -->
        <v-col cols="auto">
          <div class="control-group">
            <span class="control-group__label">{{ $t('label.display_mode') }}</span>
            <v-select
              :value="displayMode"
              :items="displayModeOptions"
              :disabled="!currentFloorPlan"
              item-value="value"
              item-text="text"
              dense
              hide-details
              outlined
              class="compact-select"
              style="width: 160px;"
              :aria-label="$t('label.display_mode')"
              @change="$emit('display-mode-change', $event)"
            />
          </div>
        </v-col>

        <!-- 検出種別 -->
        <v-col cols="auto">
          <div class="control-group">
            <span class="control-group__label">{{ $t('label.detection_filter') }}</span>
            <div
              :class="['detection-filter', { 'detection-filter--disabled': !currentFloorPlan }]"
              role="radiogroup"
              :aria-label="$t('label.detection_filter')"
            >
              <label
                v-for="option in detectionFilterOptions"
                :key="option.value"
                :class="['filter-segment', { 'filter-segment--active': detectionFilter === option.value }]"
              >
                <input
                  type="radio"
                  name="detection-filter"
                  :value="option.value"
                  :checked="detectionFilter === option.value"
                  :disabled="!currentFloorPlan"
                  class="filter-segment__input"
                  @change="$emit('detection-filter-change', option.value)"
                />
                {{ option.label }}
              </label>
            </div>
          </div>
        </v-col>

        <v-spacer />

        <!-- 図面ダウンロードボタン -->
        <v-col cols="auto" class="d-flex align-center">
          <v-btn
            icon
            small
            :loading="exporting"
            :disabled="exporting || !currentFloorPlan"
            :aria-label="$t('action.download')"
            data-testid="download-floor-plan"
            @click="$emit('download')"
          >
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
    <v-progress-linear
      v-if="loadingEvents"
      indeterminate
      color="primary"
      height="2"
      class="toolbar-progress"
    />
  </v-card>
</template>

<i18n lang="yaml">
ja:
  label:
    floor_plan: "図面:"
    no_floor_plan: 図面なし
    edit_name: 図面名を編集
    month: 表示月
    display_mode: 描画モード
    detection_filter: 検出種別
  action:
    edit_name: 名前を編集
    save_name: 図面名を保存
    add: 新規図面を追加
    delete: 図面を削除
    download: 図面を画像でダウンロード
    save: 保存
    cancel: 取り消し
  msg:
    unsaved_changes: 未保存の変更があります
    saving: 保存中...
  filter:
    exclude_misdetection: 誤検出を除外
    pickup_only: ピックアップのみ
    all: すべて
  display_mode:
    placement_only: 配置のみ
    none: 表示なし
    name: 名前のみ
    name_and_count: 名前＋検出数

en:
  label:
    floor_plan: "Floor plan:"
    no_floor_plan: No floor plan
    edit_name: Edit floor plan name
    month: Display month
    display_mode: Display mode
    detection_filter: Detection filter
  action:
    edit_name: Edit name
    save_name: Save floor plan name
    add: Add floor plan
    delete: Delete floor plan
    download: Download floor plan as image
    save: Save
    cancel: Cancel
  msg:
    unsaved_changes: You have unsaved changes
    saving: Saving...
  filter:
    exclude_misdetection: Exclude false positives
    pickup_only: Pickup only
    all: All
  display_mode:
    placement_only: Placement only
    none: None
    name: Name only
    name_and_count: Name + count
</i18n>

<script>
import MonthField from '@/components/molecules/MonthField';
import { DETECTION_FILTERS } from './detectionFilters';
import { DISPLAY_MODES } from './displayModes';

export default {
  name: 'FloorPlanToolbar',
  components: {
    MonthField,
  },
  props: {
    currentFloorPlan: {
      type: Object,
      required: false,
      default: null,
    },
    allFloorPlans: {
      type: Array,
      required: true,
    },
    selectedMonth: {
      type: String,
      required: true,
    },
    displayMode: {
      type: String,
      required: true,
    },
    detectionFilter: {
      type: String,
      required: true,
    },
    loadingEvents: {
      type: Boolean,
      default: false,
    },
    hasUnsavedChanges: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    deleting: {
      type: Boolean,
      default: false,
    },
    exporting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isEditingName: false,
      editingName: '',
    };
  },
  computed: {
    floorPlanOptions() {
      return this.allFloorPlans.map((fp) => ({ value: fp.id, text: fp.name }));
    },
    detectionFilterOptions() {
      return [
        { value: DETECTION_FILTERS.EXCLUDE_MISDETECTION, label: this.$t('filter.exclude_misdetection') },
        { value: DETECTION_FILTERS.PICKUP_ONLY, label: this.$t('filter.pickup_only') },
        { value: DETECTION_FILTERS.ALL, label: this.$t('filter.all') },
      ];
    },
    displayModeOptions() {
      return [
        { value: DISPLAY_MODES.PLACEMENT_ONLY, text: this.$t('display_mode.placement_only') },
        { value: DISPLAY_MODES.NONE, text: this.$t('display_mode.none') },
        { value: DISPLAY_MODES.NAME, text: this.$t('display_mode.name') },
        { value: DISPLAY_MODES.NAME_AND_COUNT, text: this.$t('display_mode.name_and_count') },
      ];
    },
  },
  methods: {
    startNameEdit() {
      this.editingName = this.currentFloorPlan.name;
      this.isEditingName = true;
    },
    saveNameEdit() {
      if (this.editingName.trim()) {
        this.$emit('save-name', this.editingName.trim());
      }
      this.isEditingName = false;
    },
    cancelNameEdit() {
      this.isEditingName = false;
    },
  },
};
</script>

<style scoped>
.toolbar-card {
  position: relative;
}

.toolbar-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}

.compact-select {
  font-size: 13px;
}

.compact-select ::v-deep .v-input__slot {
  min-height: 32px !important;
}

.compact-select ::v-deep .v-input__append-inner {
  margin-top: 5px !important;
}

.compact-select ::v-deep .v-select__selection,
.compact-select ::v-deep .v-text-field__slot input {
  font-size: 13px;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.control-group__label {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.5);
}

.detection-filter {
  display: inline-flex;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
}

.filter-segment {
  padding: 4px 12px;
  font-size: 13px;
  cursor: pointer;
  border-right: 1px solid #ccc;
  user-select: none;
  transition: background 0.15s, color 0.15s;
}

.filter-segment:last-child {
  border-right: none;
}

.filter-segment--active {
  background: #e3f2fd;
  color: #1976d2;
  font-weight: 500;
}

.filter-segment__input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.detection-filter--disabled {
  opacity: 0.5;
  pointer-events: none;
}
</style>
